import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { intersection } from "lodash";

import { IUser, UserRole } from "types/user";

interface IAuthContextType {
  user?: IUser;
  isLoggedIn: boolean;
  hasPermission: Function;
  logout: () => void;
  setUser: Function;
}

export const AuthContext = createContext<IAuthContextType>(
  {} as IAuthContextType
);

// Export the provider as we need to wrap the entire app with it
export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [user, setUser] = useState<IUser | undefined>();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  // Need to set it to true when an auth is implemented.
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);

  useEffect(() => {
    const userInfo = localStorage.getItem("user");
    if (userInfo) {
      setUser(JSON.parse(userInfo));
      setIsLoggedIn(true);
    }
    setLoadingInitial(false);
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      setIsLoggedIn(true);
    } else {
      localStorage.removeItem("user");
      setIsLoggedIn(false);
    }
  }, [user]);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(undefined);
  };

  const hasPermission = (roles: UserRole[]) => {
    if (!user || !user.role) return false;
    return intersection([user.role], roles).length > 0;
  };

  const memoedValue = useMemo(
    () => ({
      user,
      isLoggedIn,
      hasPermission,
      logout,
      setUser,
    }),
    /* eslint-disable react-hooks/exhaustive-deps */
    [user, isLoggedIn]
  );

  // Only render the underlying app after asserting for the presence of a current user.
  return (
    <AuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </AuthContext.Provider>
  );
}

export default function useAuth() {
  return useContext(AuthContext);
}
